import axios from "axios";
import urlconfig from "../_config";

const VERSION = "v1";

// Token config helper
const tokenConfig = () => {
  const token = localStorage.getItem("token");
  const config = {
    headers: {
      "Content-Type": "application/json",
    },
  };
  if (token) {
    config.headers["Authorization"] = `Token ${token}`;
  }
  return config;
};

// Base Slack Tasks API URL
const SLACK_TASKS_API = "https://slack-tasks.innerlogic-api.com/api";

// ==================== MY WORKSPACES ====================
export const GET_MY_SLACK_WORKSPACES = () =>
  axios.get(`${SLACK_TASKS_API}/my/workspaces`, tokenConfig());

// ==================== WORKSPACE USERS ====================
export const GET_SLACK_WORKSPACE_USERS = (workspaceId, payload) =>
  axios.get(
    `${SLACK_TASKS_API}/workspaces/${workspaceId}/users${
      payload ? `?${payload}` : ""
    }`,
    tokenConfig()
  );

// ==================== CREATE TASK ====================
export const CREATE_SLACK_TASK = (workspaceId, payload) =>
  axios.post(
    `${SLACK_TASKS_API}/workspaces/${workspaceId}/tasks`,
    payload,
    tokenConfig()
  );

// ==================== TASK HISTORY ====================
export const GET_SLACK_TASK_HISTORY = (workspaceId, userId, payload) =>
  axios.get(
    `${SLACK_TASKS_API}/workspaces/${workspaceId}/users/${userId}/history${
      payload ? `?${payload}` : ""
    }`,
    tokenConfig()
  );

export const GET_SLACK_MY_TASK_HISTORY = (workspaceId, payload) =>
  axios.get(
    `${SLACK_TASKS_API}/workspaces/${workspaceId}/my/history${
      payload ? `?${payload}` : ""
    }`,
    tokenConfig()
  );

// ==================== DAILY DIGEST ====================
export const GET_SLACK_DAILY_DIGEST = (workspaceId, userId, payload) =>
  axios.get(
    `${SLACK_TASKS_API}/workspaces/${workspaceId}/users/${userId}/digest${
      payload ? `?${payload}` : ""
    }`,
    tokenConfig()
  );

export const GET_SLACK_MY_DAILY_DIGEST = (workspaceId, payload) =>
  axios.get(
    `${SLACK_TASKS_API}/workspaces/${workspaceId}/my/digest${
      payload ? `?${payload}` : ""
    }`,
    tokenConfig()
  );

// ==================== WORKSPACE TASKS ====================
export const GET_SLACK_WORKSPACE_TASKS = (workspaceId, payload) =>
  axios.get(
    `${SLACK_TASKS_API}/workspaces/${workspaceId}/tasks${
      payload ? `?${payload}` : ""
    }`,
    tokenConfig()
  );

// ==================== TASK METRICS ====================
export const GET_SLACK_TASK_METRICS = (workspaceId, payload) =>
  axios.get(
    `${SLACK_TASKS_API}/workspaces/${workspaceId}/metrics${
      payload ? `?${payload}` : ""
    }`,
    tokenConfig()
  );

// ==================== MEETINGS ====================
export const GET_SLACK_MEETINGS = (workspaceId, payload) =>
  axios.get(
    `${SLACK_TASKS_API}/workspaces/${workspaceId}/meetings${
      payload ? `?${payload}` : ""
    }`,
    tokenConfig()
  );

export const FETCH_SLACK_MEETING = (workspaceId, meetingId) =>
  axios.get(
    `${SLACK_TASKS_API}/workspaces/${workspaceId}/meetings/${meetingId}`,
    tokenConfig()
  );

// ==================== MEETING TASKS ====================
export const GET_SLACK_MEETING_TASKS = (workspaceId, meetingId) =>
  axios.get(
    `${SLACK_TASKS_API}/workspaces/${workspaceId}/meetings/${meetingId}/tasks`,
    tokenConfig()
  );

// ==================== HELPER FUNCTIONS ====================

/**
 * Build query string from object
 * @param {Object} params - Query parameters
 * @returns {string} Query string
 */
export const buildQueryString = (params) => {
  if (!params || typeof params !== "object") return "";
  const queryString = new URLSearchParams(params).toString();
  return queryString ? `?${queryString}` : "";
};

/**
 * Get task history with date range
 * @param {string} workspaceId - Workspace ID
 * @param {string} userId - User ID (optional, uses /my/ if not provided)
 * @param {Object} options - { startDate, endDate, status }
 */
export const getTaskHistoryByDateRange = (workspaceId, userId, options = {}) => {
  const queryString = buildQueryString(options);
  
  if (userId) {
    return GET_SLACK_TASK_HISTORY(workspaceId, userId, queryString);
  }
  return GET_SLACK_MY_TASK_HISTORY(workspaceId, queryString);
};

/**
 * Get daily digest for specific date
 * @param {string} workspaceId - Workspace ID
 * @param {string} userId - User ID (optional, uses /my/ if not provided)
 * @param {string} date - Date in YYYY-MM-DD format
 */
export const getDailyDigestByDate = (workspaceId, userId, date) => {
  const queryString = buildQueryString({ date });
  
  if (userId) {
    return GET_SLACK_DAILY_DIGEST(workspaceId, userId, queryString);
  }
  return GET_SLACK_MY_DAILY_DIGEST(workspaceId, queryString);
};

/**
 * Get workspace tasks with filters
 * @param {string} workspaceId - Workspace ID
 * @param {Object} filters - { status, assignedTo, priority, limit, offset, sortBy, sortOrder }
 */
export const getWorkspaceTasksFiltered = (workspaceId, filters = {}) => {
  const queryString = buildQueryString(filters);
  return GET_SLACK_WORKSPACE_TASKS(workspaceId, queryString);
};

/**
 * Get task metrics by date range
 * @param {string} workspaceId - Workspace ID
 * @param {Object} options - { startDate, endDate, userId }
 */
export const getTaskMetricsByDateRange = (workspaceId, options = {}) => {
  const queryString = buildQueryString(options);
  return GET_SLACK_TASK_METRICS(workspaceId, queryString);
};

/**
 * Create task with notification
 * @param {string} workspaceId - Workspace ID
 * @param {Object} taskData - Task data
 */
export const createTaskWithNotification = (workspaceId, taskData) => {
  const payload = {
    ...taskData,
    notifyOnSlack: true,
  };
  return CREATE_SLACK_TASK(workspaceId, payload);
};

/**
 * Get meetings by date
 * @param {string} workspaceId - Workspace ID
 * @param {string} date - Date in YYYY-MM-DD format
 */
export const getMeetingsByDate = (workspaceId, date) => {
  const queryString = buildQueryString({ date });
  return GET_SLACK_MEETINGS(workspaceId, queryString);
};

/**
 * Get active workspace users
 * @param {string} workspaceId - Workspace ID
 */
export const getActiveWorkspaceUsers = (workspaceId) => {
  const queryString = buildQueryString({ active: true });
  return GET_SLACK_WORKSPACE_USERS(workspaceId, queryString);
};